import * as React from "react";

import { format } from "OfficeFabric/Utilities";
import { css } from "VSSUI/Util";
import * as Controls from "VSS/Controls";
import { TableMenuItem } from "VSS/Features/MarkdownToolbar/TableMenuItem";
import { getFormattedContent } from "VSS/Features/MarkdownToolbar/Utilities";
import { FeatureAvailabilityService } from "VSS/FeatureAvailability/Services";
import { registerLWPComponent } from "VSS/LWP";
import { publishEvent } from "VSS/Telemetry/Services";
import { announce } from "VSS/Utils/Accessibility";

import { MarkdownConstants } from "ContentRendering/MarkdownConstants";
import { MarkdownStatusBar } from "ContentRendering/MarkdownTextArea/MarkdownStatusBar";
import { MarkdownToolbar, MarkdownToolbarCommand } from "ContentRendering/MarkdownToolbar/MarkdownToolbar";
import { MarkdownToolbarHelper } from "ContentRendering/MarkdownToolbar/Utility";
import { MarkdownToolbarMentionCommands, MentionCommandType } from "Discussion/Scripts/MarkdownToolbarMentionCommands";
import { AutocompleteEnhancement} from "Mention/Scripts/TFS.Mention.Autocomplete.Controls";
import { LearningBubble } from "Presentation/Scripts/TFS/Components/LearningBubble/LearningBubble";
import { LearningBubbleSettingsKeys } from "Presentation/Scripts/TFS/Components/LearningBubble/Constants";
import { getMermaidMenuItem } from "Presentation/Scripts/TFS/Extension/MermaidOrchestrator";
import { getVideoMenuItem } from "Presentation/Scripts/TFS/Extension/Orchestrator";
import { getWitQueryMenuItem } from "Presentation/Scripts/TFS/Extension/WitQueryTableOrchestrator";
import { TfsContext } from "Presentation/Scripts/TFS/TFS.Host.TfsContext";
import { TemplateConstants } from "Wiki/Scripts/CommonConstants";
import { Areas, TelemetryConstants, WikiTypeCI } from "Wiki/Scripts/CustomerIntelligenceConstants";
import * as WikiResources from "Wiki/Scripts/Resources/TFS.Resources.Wiki";
import { createWikiEventData } from "Wiki/Scenarios/Shared/Sources/TelemetryWriter";

import "VSS/LoaderPlugins/Css!Wiki/Scenarios/Overview/Components/WikiToolbar";

export interface WikiToolbarProps {
    validAttachmentTypes: string[];
    getTextArea(): HTMLTextAreaElement;
    onBrowseAttachments(files: File[]): void;
    textChangedByToolbar(text: string, selectionStart: number, selectionEnd: number): void;
    wikiType: WikiTypeCI;
    newWiki?: boolean;
    showLearningBubble?: boolean;
    disableInsertTextCommand?: boolean;
    showMermaidCommand?: boolean;
    pageContext?: any;
    className?: string;
    isIncludeEditingAllowed?: boolean;
    onPasteAsHtmlToggle?(): void;
    showSyncScrollbutton?: boolean;
    isSyncScrollOn?: boolean;
    disableSyncScrollButton?: boolean;
    onToggleSyncScroll?: () => void;
}

export interface WikiToolbarState {
    pasteTextType: PasteType;
    textWrapType: WrapType;
}

export enum PasteType {
    text,
    html,
}

export enum WrapType {
    normal,
    pre,
}

const IncludesMenuItemKey = "includes-menu-item";
const IncludesSubstring1 = "::: {0} ";
const IncludesSubstring2 = "\n:::";
const TOCMenuItemKey = "toc-menu-item";
const TOSPMenuItemKey = "tosp-menu-item";
const PasteMenuItemKey = "paste-menu-item";
const TableMenuItemKey = "table-menu-item";
const WrapTextMenuItemKey = "wraptext-menu-item";
const synchronizeScrollMenuItemKey = "synchronize-scroll-menu-item";

export class WikiToolbar extends React.PureComponent<WikiToolbarProps, WikiToolbarState> {
    private _toolbar: MarkdownToolbar;
    private _markdownToolbarMentionCommands: MarkdownToolbarMentionCommands;
    private _telemetryProperties: { [key: string]: any };
    private _mermaidLearningBubble = React.createRef<LearningBubble>();

    constructor(props: WikiToolbarProps) {
        super(props);
        this.state = {
            pasteTextType: PasteType.text,
            textWrapType: WrapType.normal
        };
        this._markdownToolbarMentionCommands = new MarkdownToolbarMentionCommands(this._getTextArea, this._textChangedByToolbar);
        this._setTelemetryProperties();
    }

    public componentDidMount(): void {
        if (this._mermaidLearningBubble.current) {
            this._mermaidLearningBubble.current.showIfNeededAfterDelay(1000);
        }
    }

    public componentWillUnMount(): void {
        if (this._mermaidLearningBubble.current) {
            this._mermaidLearningBubble.current.cancelDelayedShow();
        }
    }

    public render(): JSX.Element {
        return (
            <div className={css("wiki-markdown-toolbar bowtie", this.props.className)}>
                <MarkdownToolbar
                    ref={this._refToolbar}
                    addAttachments={this._onBrowseAttachments}
                    getTextArea={this._getTextArea}
                    textChanged={this._textChangedByToolbar}
                    validAttachmentTypes={this.props.validAttachmentTypes}
                    commands={this._extraToolbarCommands()}
                    overflowCommands={this._overflowToolbarCommands()}
                    enableYAMLTags={true}
                    enableFormula={true}
                    menuItemClickedCallback={this._menuItemTelemetry}
                    disableInsertTextCommand={this.props.disableInsertTextCommand}
                />
                <MarkdownStatusBar
                    markdownDocumentLink={WikiResources.MarkdownDocumentLink}
                    telemetryArea={Areas.Wiki}
                    telemetryProperties={this._telemetryProperties}
                    enableAttachments={false}
                />
                {this.props.showLearningBubble && this.props.showMermaidCommand && TfsContext.getDefault().isHosted && (
                    <LearningBubble
                        ref={this._mermaidLearningBubble}
                        settingsKey={LearningBubbleSettingsKeys.WikiMermaidDiagram}
                        target={".wiki-markdown-toolbar .insrt-mermaid-dgrm-btn i"}
                        text={WikiResources.MermaidLearningBubbleText}
                        buttonLabel={WikiResources.MermaidLearningBubbleCtaLabel}
                    />
                )}
            </div>);
    }


    private _setTelemetryProperties(): void {
        const projectId: string = TfsContext.getDefault().contextData.project.id;
        this._telemetryProperties = { projectId: projectId, wikiType: this.props.wikiType, newWiki: this.props.newWiki };
    }

    public get shouldPasteHtml(): boolean {
        return PasteType.html === this.state.pasteTextType;
    }

    public handleKeyPress(event: React.KeyboardEvent<HTMLElement>): void {
        if (event.ctrlKey && event.shiftKey && (event.key === "F" || event.key === "f")) { // format tables shortcut
            const textArea = this.props.getTextArea();
            const { tablesCount, content, selectionStart, selectionEnd } = getFormattedContent(
                textArea.value,
                textArea.selectionStart,
                textArea.selectionEnd
            );
            this._onTablesFormatted(tablesCount, content, selectionStart, selectionEnd, true);
            event.preventDefault();
            event.stopPropagation();
        } else {
            this._toolbar.handleKeyPress(event);
        }
    }

    public insertText(insertedString: string, requirePreSpace: boolean): void {
        this._toolbar.insertText(insertedString, { requirePreSpace: requirePreSpace });
    }

    private _extraToolbarCommands(): MarkdownToolbarCommand[] {
        const commands: MarkdownToolbarCommand[] = this._markdownToolbarMentionCommands.getCommands([MentionCommandType.WorkItem], false, this._menuItemTelemetry);

        const isCheckedState = (this.state.pasteTextType === PasteType.html);
        commands.push({
            key: PasteMenuItemKey,
            name: WikiResources.MarkdownPasteHTML,
            className: css("markdowntoolbar-button", { is_checked: isCheckedState }),
            icon: "PasteAsCode",
            onClick: this._togglePasteState,
            title: WikiResources.MarkdownPasteHTML,
            canCheck: true,
            checked: isCheckedState,
            ariaLabel: WikiResources.MarkdownPasteHTML,
        });

        if (this.props.showSyncScrollbutton) {
            let checked = this.props.isSyncScrollOn;

            let synchronizeScrollMenuTitle = this.props.isSyncScrollOn
                ? WikiResources.SynchronizeScrollDisableText
                : WikiResources.SynchronizeScrollEnableText;

            if (this.props.disableSyncScrollButton) {
                synchronizeScrollMenuTitle = WikiResources.DisableSynchronizeScrollToggle;
                checked = undefined;
            }

            commands.push({
                key: synchronizeScrollMenuItemKey,
                name: WikiResources.SynchronizedScroll,
                className: css("markdowntoolbar-button", { is_checked: checked}),
                icon: "ScrollUpDown",
                onClick: this._onToggleSyncScroll,
                title: synchronizeScrollMenuTitle,
                canCheck: !this.props.disableSyncScrollButton,
                checked: checked,
                ariaLabel: synchronizeScrollMenuTitle,
                index: 8,
                inactive: this.props.disableSyncScrollButton
            });
        }

        const isWordWrapEnabled = (this.state.textWrapType === WrapType.normal);
        const wrapMenuName = isWordWrapEnabled ? WikiResources.MarkdownUnwrapText:WikiResources.MarkdownWrapText;

        commands.push({
            key: WrapTextMenuItemKey,
            name: wrapMenuName,
            className: css("markdowntoolbar-button", { is_checked: isWordWrapEnabled }),
            icon: "TextField",
            onClick: this._toggleTextWrapState,
            title: wrapMenuName,
            canCheck: true,
            checked: isWordWrapEnabled,
            ariaLabel: wrapMenuName,
            index: 8
        });

        commands.push({
            key: TableMenuItemKey,
            name: WikiResources.MarkdownTable,
            index: 8,
            onRender: () => {
                return (
                    <TableMenuItem
                        onTableSelected={this._onTableSelected}
                        onTablesFormatted={this._onTablesFormatted}
                        getTextArea ={this.props.getTextArea}
                        onMenuItemClicked={this._onTableMenuItemClicked}
                        iconClassName="insert-table-icon font-size-ml"
                        buttonClassName="insert-table-button"
                        ariaPosInSet={9} // ariaPosInSet and setSize needs to be updated when we add new menu items to wiki toolbar.
                        ariaSetSize={14}
                    />
                );
            }
        });

        if (this.props.showMermaidCommand) {
          const mermaidMenuItem = getMermaidMenuItem(this._getTextArea, this._textChangedByToolbar, this._menuItemTelemetry);
          commands.push(mermaidMenuItem);
        }

        return commands;
    }

    private _onToggleSyncScroll = (): void => {
        this._menuItemTelemetry(synchronizeScrollMenuItemKey, { syncScrollEnabled: !this.props.isSyncScrollOn });
        this.props.onToggleSyncScroll();
    };

    private _onTableMenuItemClicked = (): void => {
        this._menuItemTelemetry(TableMenuItemKey);
    }

    private _onTablesFormatted = (tablesCount: number, content?: string, selectionStart?: number, selectionEnd?: number, isShortcutKey?: boolean): void => {
        const textArea = this.props.getTextArea();
        if (tablesCount > 0) {
            if (MarkdownToolbarHelper.browserSupportsInsertText()) {
                textArea.focus();
                textArea.selectionStart = 0;
                textArea.selectionEnd = textArea.value.length;
                document.execCommand("insertText", false, content);
                this._textChangedByToolbar(null, selectionStart, selectionEnd);
            } else {
                textArea.focus();
                this._textChangedByToolbar(content, selectionStart, selectionEnd);
            }
            textArea.blur();
            textArea.focus(); // blur and focus to keep the cursor position in view after formatting tables.
            announce(WikiResources.TablesFormattedText);
            announce(" "); // hack to make nvda announce on shortcut press.
        }
        publishEvent(createWikiEventData(TelemetryConstants.TablesFormatted, { tablesCount, isShortcutKey, newWiki: this.props.newWiki }, this.props.wikiType));
    }

    private _onTableSelected = (tableString: string, rowCount: number, colCount: number): void => {
        MarkdownToolbarHelper.insertTextAtStartOfLineOrDoc(
            this.props.getTextArea,
            tableString,
            (text: string, selectionStart: number, selectionEnd: number) => this._insertTable(text, selectionStart, tableString));

        if (MarkdownToolbarHelper.browserSupportsInsertText()) {
            const textArea = this.props.getTextArea();
            const index = textArea.value.slice(0, textArea.selectionStart).lastIndexOf(tableString);
            const selectionStartIndex = index + 2; // skip newline and the first "|"
            this._textChangedByToolbar(null, selectionStartIndex, selectionStartIndex + 2);
        }

        announce(format(WikiResources.TableInsertedText, colCount, rowCount));

        publishEvent(createWikiEventData(TelemetryConstants.TableInserted, { rows: rowCount, columns: colCount, newWiki: this.props.newWiki }, this.props.wikiType));
    }

    private _insertTable = (text: string, selectionStart: number, tableString: string): void => {
        const index = text.slice(0, selectionStart).lastIndexOf(tableString);
        const selectionStartIndex = index + 2; // skip newline and the first "|"
        this._textChangedByToolbar(text, selectionStartIndex, selectionStartIndex + 2); // select the first cell in the table
    }

    private _overflowToolbarCommands(): MarkdownToolbarCommand[] {
        let commands: MarkdownToolbarCommand[] = [{
            index: 0,
            key: TOCMenuItemKey,
            name: WikiResources.TOCText,
            className: css("markdowntoolbar-button"),
            icon: "CustomList",
            onClick: this._onTocMenuItemClick,
            title: WikiResources.InsertTOCText,
            ariaLabel: WikiResources.InsertTOCText,
        }];
        if (FeatureAvailabilityService.isFeatureEnabled("Wiki.TableOfSubPages", false)) {
            commands.push({
                index: 1,
                key: TOSPMenuItemKey,
                name: WikiResources.TOSPText,
                className: css("markdowntoolbar-button"),
                icon: "CustomList",
                onClick: this._onTospMenuItemClick,
                title: WikiResources.InsertTOSPText,
                ariaLabel: WikiResources.InsertTOSPText,
            });
        }

        const videoCommand = getVideoMenuItem(this._getTextArea, this._textChangedByToolbar, commands[commands.length - 1].index + 1, this._menuItemTelemetry);
        commands.push(videoCommand);

        const mentionCommands = this._markdownToolbarMentionCommands.getCommands(
            [MentionCommandType.People],
            true,
            this._menuItemTelemetry
        );
        commands.push(mentionCommands[0]);

         if (this.props.isIncludeEditingAllowed) {
            commands.push({
                key: IncludesMenuItemKey,
                name: WikiResources.IncludesText,
                className: css("markdowntoolbar-button"),
                icon: "FileTemplate",
                onClick: this._onIncludeMenuItemClick,
                title: WikiResources.InsertIncludesText,
                ariaLabel: WikiResources.InsertIncludesText,
            });
        }

        const witQueryMenuItem = getWitQueryMenuItem(this._getTextArea, this._textChangedByToolbar, this._menuItemTelemetry, this.props.pageContext);
        commands.push(witQueryMenuItem);
        return commands;
    }

    private _togglePasteState = () => {
        const checkedHtml = "pasteTypeHtml";
        const unCheckedHtml = "pasteTypeText";

        if (this.state.pasteTextType === PasteType.text) {
            this._menuItemTelemetry(PasteMenuItemKey, { PasteType: checkedHtml });
            this.setState({ pasteTextType: PasteType.html });
        } else {
            this._menuItemTelemetry(PasteMenuItemKey, { PasteType: unCheckedHtml });
            this.setState({ pasteTextType: PasteType.text });
        }
        if (this.props.onPasteAsHtmlToggle)
        {
            this.props.onPasteAsHtmlToggle();
        }
    }

    private _toggleTextWrapState = (): void => {
        const textArea = this.props.getTextArea();
        const nowrapClassName = "wiki-ta-nowrap";

        if (this.state.textWrapType === WrapType.normal) {
            textArea.classList.add(nowrapClassName);
            // for it to work in IE, we need to use wrap attribute
            textArea.setAttribute("wrap","off");

            this._menuItemTelemetry(WrapTextMenuItemKey, { WrapType: "pre" });
            this.setState({ textWrapType: WrapType.pre });
            announce(WikiResources.WordWrapDisabledText);

        } else {
            textArea.classList.remove(nowrapClassName);
            textArea.removeAttribute("wrap");

            this._menuItemTelemetry(WrapTextMenuItemKey, { WrapType: "normal" });
            this.setState({ textWrapType: WrapType.normal });
            announce(WikiResources.WordWrapEnabledText);

        }
    }

    private _onTocMenuItemClick = () => {
        this._onInsertTagButtonsClick(MarkdownConstants.TOCDefaultMarker, TOCMenuItemKey);
    }

    private _onTospMenuItemClick = () => {
        this._onInsertTagButtonsClick(MarkdownConstants.TOSPDefaultMarker, TOSPMenuItemKey);
    }

    private _onInsertTagButtonsClick(tag: string, menuItemKey: string): void {
        MarkdownToolbarHelper.insertTextAtStartOfLineOrDoc(this._getTextArea, tag, this._textChangedByToolbar);
        this._menuItemTelemetry(menuItemKey);
    }

    private _onIncludeMenuItemClick = () => {
        const includesSnippet = format(IncludesSubstring1, TemplateConstants.Identifier) + IncludesSubstring2;

        MarkdownToolbarHelper.insertTextAtStartOfLineOrDoc(this._getTextArea, includesSnippet, this._textChangedByToolbar);
        const textArea = this.props.getTextArea();
        const index = textArea.value.slice(0, textArea.selectionStart).lastIndexOf(includesSnippet);
        const newCursorIndex = index + textArea.value.slice(index, textArea.selectionStart).indexOf("\n");
        //update cursor position
        this._textChangedByToolbar(null, newCursorIndex, newCursorIndex);
        let enhancement = Controls.Enhancement.getInstance(AutocompleteEnhancement, $(textArea)) as AutocompleteEnhancement;
        if (enhancement) {
            enhancement.runAutocomplete({
                textBeforeSelection: textArea.value.slice(0, newCursorIndex),
                textInSelection: "",
                textAfterSelection: textArea.value.slice(newCursorIndex, textArea.value.length)
            });
        }

        this._menuItemTelemetry(IncludesMenuItemKey);
    }

    private _menuItemTelemetry = (menuItemKey: string, additionalProperties?: IDictionaryStringTo<string | boolean>) => {
        const properties = { [TelemetryConstants.MenuItemKey]: menuItemKey, newWiki: this.props.newWiki, ...additionalProperties };
        publishEvent(createWikiEventData(TelemetryConstants.MenuItemAddedToPageContent, properties, this.props.wikiType));
    }

    private _getTextArea = (): HTMLTextAreaElement => {
        return this.props.getTextArea();
    }

    private _onBrowseAttachments = (files: File[]) => {
        this.props.onBrowseAttachments(files);
    }

    private _refToolbar = (toolbar: MarkdownToolbar) => {
        this._toolbar = toolbar;
    }

    private _textChangedByToolbar = (text: string, selectionStart: number, selectionEnd: number) => {
        this.props.textChangedByToolbar(text, selectionStart, selectionEnd);
    }
}

registerLWPComponent("TFS.Wiki.WikiToolBar", WikiToolbar);
